<template>
    <div>
        <ProjectJSONLD :project="project" />
        <section
            class="hero is-primary css-hero"
            :style="{
                backgroundImage: 'url(' + project.hero + ')',
                backgroundPosition: project.heroPosition || 'center center',
            }">
        </section>
        <div class="container">
            <div class="section">
                <nav
                    class="breadcrumb has-dot-separator"
                    aria-label="breadcrumbs">
                    <ul>
                        <li>
                            <router-link to="/projects">PROJECTS</router-link>
                        </li>
                        <li class="is-active">
                            <a href="#" aria-current="page">{{
                                project.name
                            }}</a>
                        </li>
                    </ul>
                </nav>

                <div class="level">
                    <div class="level-left">
                        <div class="level-item">
                            <h1 class="title">
                                {{ project.name }}
                            </h1>
                        </div>
                    </div>
                    <div class="level-right">
                        <div class="level-item">
                            <doi-badge :doi="project.doi"  v-if="(project.doi || []).length"/>
                        </div>
                    </div>
                </div>

                <h2 class="subtitle">{{ project.blurb }}</h2>
                <div class="tags">
                    <span
                        v-for="tag in project.tags"
                        :key="tag"
                        class="tag is-rounded is-success is-light">
                        <router-link
                            :to="{
                                name: 'projects',
                                query: { selectedTags: JSON.stringify([tag]) },
                            }">
                            {{ tag }}
                        </router-link>
                    </span>
                </div>
                <div>
                    <div style="margin: 0.5em 0">
                        <span>
                            <DataTypeIcon dataType="volumetric" />
                            &nbsp;
                            <DataTypeIcon
                                v-if="(project.segmentation || []).length"
                                dataType="segmentation" />
                            &nbsp;
                            <DataTypeIcon
                                v-if="(project.connectomes || []).length"
                                dataType="connectomics" />
                        </span>
                    </div>
                </div>
                <div class="columns">
                    <div class="column is-7">
                        <div v-if="internDownloadCode">
                        <div class="code-flex-wrapper">
                        <button class="copy-side-button" @click="copyToClipboard">
                            Copy Snippet 📋
                        </button>
                        <highlight-code lang="python">
                            {{ internDownloadCode }}
                        </highlight-code>
                        </div>
                            <div class="intern-docs-text">
                                <small>
                                    <b>intern</b>
                                    documentation is available
                                    <a
                                        href="https://github.com/jhuapl-boss/intern/wiki"
                                        >here</a
                                    >.
                                </small>

                                <a class="colab-button" target="_blank" :href="collabURL" v-if="collabURL">
                                        <img src="https://colab.research.google.com/assets/colab-badge.svg" alt="Open In Colab"/>
                                </a>
                            </div>

                        </div>
                        <div class="section">
                            <div
                                class="markdown"
                                v-for="(pgraph, i) in markdownParagraphs"
                                :key="i">
                                <div class="content" v-html="pgraph"></div>
                            </div>
                        </div>
                        <DataCard :collection_ids="project.collections" />
                    </div>

                    <div class="column is-5">
                        <div class="columns is-multiline is-variable is-1">
                            <div class="column">
                                <a target="_blank" :href="mgmtURL" v-if="mgmtURL">
                                    <button class="button is-link is-fullwidth">
                                        <i class="fas fa-external-link-alt"> </i>
                                        &nbsp; Inspect on BossDB.io
                                    </button>
                                </a>
                            </div>
                            <div class="column">
                                <a target="_blank" :href="metadataURL" v-if="metadataURL">
                                    <button class="button is-dark is-fullwidth">
                                        View Metadata
                                    </button>
                                </a>
                            </div>
                        </div>
                        <br />
                        <div class="card-content">
                            <a :href="imageLink" target="_blank">
                                <div class="neuroglancer-autoload">
                                    <div
                                        class="
                                            neuroglancer-autoload-hover
                                        "></div>
                                    <img
                                        :src="previewImageSrc"
                                        @error="usePreviewFallback"
                                        :alt="project.name + ' data preview'"
                                        class="image" />
                                </div>
                            </a>
                        </div>
                        <br />
                        <ProjectInfoCard :project="project" />
                        <br />
                        <div class="card" v-if="project.brainloc">
                            <header class="card-header">
                                <p class="card-header-title">
                                    Physical Location
                                </p>
                            </header>
                            <div class="card-content">
                                <div
                                    class="center-image"
                                    v-if="project.brainloc">
                                    <img
                                        :src="
                                            ASSETS_BASE_URL + 'locations/' +
                                                project.brainloc +
                                                '.svg'
                                        " />
                                </div>
                            </div>
                        </div>

                        <br />

                        <div class="card" v-if="project.publications.length">
                            <header class="card-header">
                                <p class="card-header-title">Publications</p>
                            </header>
                            <div class="card-content">
                                <div
                                    class="content"
                                    v-for="publication in project.publications"
                                    :key="publication.name">
                                    <a :href="publication.url">{{
                                        publication.name
                                    }}</a
                                    >.
                                    <cite v-if="publication.citationName">
                                        {{ publication.citationName }}
                                    </cite>
                                    <cite v-else>
                                        {{ publication.authors[0] }} et al.
                                    </cite>
                                    {{
                                        publication.venue
                                            ? `(${publication.venue})`
                                            : ""
                                    }}
                                </div>
                            </div>
                        </div>

                        <br />
                        <br />

                        <div v-if="coordinateFrames.length">
                            <CoordinateFrameCard
                                :coordinateFrames="coordinateFrames" />
                            <br />
                            <br />
                        </div>

                        <div class="card" v-if="project.links.length">
                            <header class="card-header">
                                <p class="card-header-title">Links</p>
                            </header>
                            <div class="card-content">
                                <div
                                    class="content"
                                    v-for="link in project.links"
                                    :key="link.name">
                                    <a :href="link.url">{{ link.name }}</a>
                                </div>
                                <hr />
                                <small>
                                    For anonymous read-only access to BossDB,
                                    use username
                                    <code>public-access</code> and password
                                    <code>public</code>
                                </small>
                                <br />
                            </div>
                        </div>

                        <br />
                        <br />

                        <div
                            id="extra-connectomes"
                            class="card"
                            v-if="(project.connectomes || []).length">
                            <header class="card-header">
                                <p class="card-header-title">
                                    <span style="margin-right: 1em">
                                        <ConnectomeIcon height="1.75em" />
                                    </span>
                                    Connectomes
                                </p>
                            </header>
                            <div class="card-content">
                                <br />
                                <div
                                    class="content"
                                    v-for="connectome in project.connectomes"
                                    :key="connectome.name">
                                    <a target="_blank" :href="connectome.url"
                                        >{{
                                            connectome.type == "MotifStudio"
                                                ? "Query"
                                                : "Download"
                                        }}
                                        {{ connectome.name }} in
                                        {{ connectome.type }}</a
                                    >
                                </div>
                            </div>
                        </div>

                        <div v-if="(project.connectomes || []).length">
                            <br />
                            <br />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import showdown from "showdown";

import { ASSETS_BASE_URL } from '@/assets.js';
import MetadataRemote from "@/metadata.js";
import ConnectomeIcon from "./datatypes/ConnectomeIcon.vue";
import DataTypeIcon from "./datatypes/DataTypeIcon.vue";
import DoiBadge from "./project_page/DoiBadge.vue"
import CoordinateFrameCard from "./project_page/CoordinateFrameCard.vue";
import ProjectInfoCard from "./project_page/ProjectInfoCard.vue";
import ProjectJSONLD from "./project_page/ProjectJSONLD.vue";
import DataCard from "./project_page/data/DataCard.vue";
import { Notyf } from 'notyf';
import 'notyf/notyf.min.css';
const notyf = new Notyf();

export default {
    props: {
        project: {
            type: Object,
        },
    },

    components: {
        ConnectomeIcon,
        DataTypeIcon,
        DoiBadge,
        CoordinateFrameCard,
        DataCard,
        ProjectInfoCard,
        ProjectJSONLD,
    },

    created() {
        document.title = `BossDB.org | ${this.project.name}`;

        let metadataRemote =  new MetadataRemote();

        this.coordinateFrames = this.project.experiments
            ? this.project.experiments.map(e => ({
                  experimentName: e.name,
                  link: e.bossdb_uri,
                  ...e.coordinate_frame
              }))
            : [];

        metadataRemote
            .getProject(this.project.id).then((blob) => {
                this.metadataURL = `https://metadata.bossdb.org/Project/${blob.id}`;
            }).catch((error) => {
                console.error(`Unable to fetch metadata for ${this.project.id}. ${error}`)
                this.metadataURL = undefined;
            })

                },

    data() {
        let internDownloadCode = undefined;
        let mgmtURL = undefined;
        let collabURL = undefined;
        // Is this line necessary?
        let metadataURL = this.metadataURL;

        let imageLink = this.project.neuroglancer
            ? this.project.neuroglancer.URI.replace(
                'https://neuroglancer.bossdb.io/#!',
                'https://www.neuroglass.io/new#!'
            )
            : undefined;
        if (this.project.locations && this.project.locations.length) {
            let loc = this.project.locations[0];
            if (loc.xs && loc.ys && loc.zs) {
                loc.res = loc.res || 0;
                let uri = loc.uri.split("/");
                let state = {
                    layers: [
                        {
                            source: `boss://https://api.bossdb.io/${loc.uri}`,
                            type: "image",
                            name: uri[2],
                        },
                    ],
                    navigation: {
                        pose: {
                            position: {
                                voxelCoordinates: [
                                    Math.round((loc.xs[1] + loc.xs[0]) / 2),
                                    Math.round((loc.ys[1] + loc.ys[0]) / 2),
                                    Math.round((loc.zs[1] + loc.zs[0]) / 2),
                                ],
                            },
                        },
                        zoomFactor: 8,
                    },
                    showAxisLines: false,
                    layout: "xy",
                };

                imageLink =
                    imageLink ||
                    `https://www.neuroglass.io/new#!` + JSON.stringify(state);

                internDownloadCode = `# Import intern (pip install intern)
from intern import array

# Save a cutout to a numpy array in ZYX order:
channel = array("bossdb://${uri[0]}/${uri[1]}/${uri[2]}")
data = channel[${loc.zs.join(":")}, ${loc.ys.join(":")}, ${loc.xs.join(":")}]
`;

                mgmtURL = `https://api.bossdb.io/v1/mgmt/resources/${uri[0]}`
                collabURL = `https://colab.research.google.com/github/aplbrain/bossdb-collab-notebooks/blob/main/notebooks/notebook_${this.project.id}.ipynb`
            }
        }
        let converter = new showdown.Converter({
            simplifiedAutoLink: true,
            tables: true,
            openLinksInNewWindow: true,
        });
        let markdownParagraphs = this.project.description.map((i) =>
            converter.makeHtml(i)
        );
        const previewBaseURL =
            ASSETS_BASE_URL +
            'projects/neuroglancer-preview/' +
            encodeURIComponent(this.project.id);
        return {
            ASSETS_BASE_URL,
            internDownloadCode,
            imageLink,
            markdownParagraphs,
            metadataURL,
            mgmtURL,
            collabURL,
            previewBaseURL,
            previewImageSrc: previewBaseURL + '.jpg',
        };
    },
    methods: {
        usePreviewFallback() {
            const pngPreview = this.previewBaseURL + '.png';
            const fallback = ASSETS_BASE_URL + 'placeholder.png';

            if (this.previewImageSrc !== pngPreview && this.previewImageSrc !== fallback) {
                this.previewImageSrc = pngPreview;
                return;
            }

            if (this.previewImageSrc !== fallback) {
                this.previewImageSrc = fallback;
            }
        },
        async copyToClipboard() {
            try {
                await navigator.clipboard.writeText(this.internDownloadCode);
                notyf.success('Copied snippet to clipboard.');
            } catch (err) {
                console.error('Failed to copy snippet:', err);
                notyf.error('Unable to copy snippet.');
            }
        }
    }

};
</script>


<style lang="scss" scoped>
.css-hero {
    background-size: cover;
    background-repeat: no-repeat;
    min-height: 50vh;
}

.intern-docs-text {
    margin-left: 1.5em;
}


.colab-button{
    float: right;
}
.center-image {
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 30em;
}

.markdown {
    .content {
        margin: 0 0 16px;
    }
}

.neuroglancer-autoload {
  position: relative;
  aspect-ratio: 256 / 100;
  overflow: hidden;
  border-radius: 1em;
  transition: transform 0.5s ease-in-out;

  .image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  // tweak these two to taste
  --ng-badge-pad-x: 0.9em;
  --ng-logo-pill-w: 6.2em;
  --ng-logo-pill-h: 1.35em;
  --ng-logo-gap: 0.45em;

  &:hover {
    cursor: pointer;
    transform: scale(1.05);

    &::before {
      box-shadow: 0 0 0.5em 0.5em rgba(0, 0, 0, 0.1);
    }
  }

  &::before {
    content: "🔍 View on";
    pointer-events: none;

    color: black;
    background: #dededecc;
    border-radius: 1em;
    font-weight: bold;

    position: absolute;
    bottom: 1em;
    right: 1em;
    z-index: 999;

    // normal padding + reserve space for the logo pill on the right
    padding: 0.25em var(--ng-badge-pad-x);
    padding-right: calc(var(--ng-badge-pad-x) + var(--ng-logo-pill-w) + var(--ng-logo-gap));
  }

  &::after {
    content: "";
    pointer-events: none;

    position: absolute;
    bottom: calc(1em + 0.4em); // nudges it to vertically center in the badge
    right: calc(1em + var(--ng-badge-pad-x));

    width: var(--ng-logo-pill-w);
    height: var(--ng-logo-pill-h);

    // pill around the logo
    background-color: transparent;
    border-radius: 999px;

    // logo itself
    background-image: url("https://bossdb-datalake.s3.us-east-1.amazonaws.com/public/neuroglass-logo.svg");
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;

    z-index: 1000;
  }
}


.code-flex-wrapper {
    position: relative;
}

.copy-side-button {
    position: absolute;
    top: 30px;
    right: 2rem;
    background-color: transparent;
    color: white;
    border: 1px solid #3273dc;
    border-radius: 6px;
    padding: 0.35rem 0.75rem;
    font-size: 0.75rem;
    z-index: 10;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
}

.copy-side-button:hover {
    background-color: rgba(50, 115, 220, 0.1);
}
</style>
